import { useState, memo } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, ArrowRight } from "lucide-react";
import PaymentIcons from "@/components/PaymentIcons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const shopLinks = [
  { label: "Bags", href: "/bags" },
  { label: "Clothing", href: "/clothing" },
  { label: "Accessories", href: "/accessories" },
  { label: "NOTT Atelier", href: "/atelier" },
  { label: "Workshops & DIY", href: "/workshops" },
  { label: "All Products", href: "/shop" },
];

const infoLinks = [
  { label: "About NOTT", href: "/about" },
  
  { label: "Journal", href: "/blog" },
  { label: "Size & Fit Guide", href: "/size-fit" },
  { label: "Care Guide", href: "/care" },
  { label: "FAQ & Help", href: "/faq" },
  { label: "Shipping & Returns", href: "/shipping-returns" },
  { label: "Contact Us", href: "/contact" },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
];

const Footer = memo(() => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: { email, source: "footer" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSubmitted(true);
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-foreground border-t border-primary-foreground/5">
      <div className="container px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-8 md:gap-6">
          {/* Brand column */}
          <div className="sm:col-span-2 md:col-span-4">
            <Link to="/" className="font-display text-2xl font-bold text-primary-foreground mb-4 block">
              NOTT
            </Link>
            <p className="font-body text-xs text-primary-foreground/40 leading-relaxed mb-5 max-w-xs">
              Hand-crocheted statement bags made in Amsterdam. Slow fashion, crafted with intention, made to last.
            </p>

            <div className="space-y-2">
              <a
                href="https://www.instagram.com/nott_bags"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary-foreground/40 hover:text-primary-foreground/70 transition-colors group"
                aria-label="@nott_bags – Follow NOTT on Instagram"
              >
                <Instagram size={14} />
                <span className="font-body text-xs group-hover:underline">@nott_bags</span>
              </a>
              <a
                href="mailto:info@thisisnott.com"
                className="flex items-center gap-2 text-primary-foreground/40 hover:text-primary-foreground/70 transition-colors group"
              >
                <Mail size={14} />
                <span className="font-body text-xs group-hover:underline">info@thisisnott.com</span>
              </a>
              <div className="flex items-center gap-2 text-primary-foreground/40">
                <MapPin size={14} />
                <span className="font-body text-xs">Amsterdam, NL</span>
              </div>
            </div>

            <div className="mt-5">
              <PaymentIcons variant="light" />
            </div>
          </div>

          {/* Shop links */}
          <div className="md:col-span-2 md:col-start-6">
            <h4 className="font-body text-[10px] tracking-[0.25em] uppercase text-primary-foreground/30 mb-4">
              Shop
            </h4>
            <ul className="space-y-2">
              {shopLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="font-body text-xs text-primary-foreground/50 hover:text-primary-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info links */}
          <div className="md:col-span-2">
            <h4 className="font-body text-[10px] tracking-[0.25em] uppercase text-primary-foreground/30 mb-4">
              Info
            </h4>
            <ul className="space-y-2">
              {infoLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="font-body text-xs text-primary-foreground/50 hover:text-primary-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter signup */}
          <div className="sm:col-span-2 md:col-span-2">
            <h4 className="font-body text-[10px] tracking-[0.25em] uppercase text-primary-foreground/30 mb-4">
              The Stitch
            </h4>
            <p className="font-body text-xs text-primary-foreground/40 leading-relaxed mb-4">
              Join for 10% off your first order & studio updates.
            </p>
            {submitted ? (
              <p className="font-body text-xs text-primary-foreground/60">You're in! Check your inbox ✓</p>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-0">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 h-9 bg-transparent border-primary-foreground/15 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-primary-foreground/30 rounded-none text-xs"
                />
                <Button
                  type="submit"
                  size="sm"
                  className="h-9 px-3 bg-primary-foreground text-foreground hover:bg-primary-foreground/90 rounded-none"
                  aria-label="Subscribe to newsletter"
                >
                  <ArrowRight size={14} />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div>
        <div className="container px-6 py-3 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="font-body text-[10px] text-primary-foreground/30">
            © 2026 NOTT. All rights reserved. Hand-crocheted in Amsterdam.
          </p>
          <div className="flex items-center gap-4">
            {legalLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="font-body text-[10px] text-primary-foreground/30 hover:text-primary-foreground/50 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
